import { useRef, useState, type KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { LookupManager } from '@/pages/settings/LookupManager';
import type { LookupResourceKey } from '@/api/lookups.api';
import './LookupsPage.css';

interface TabDef {
  key: LookupResourceKey;
  labelKey: string;
}

const TABS: TabDef[] = [
  { key: 'grades', labelKey: 'lookupsSettings.tabs.grades' },
  { key: 'schools', labelKey: 'lookupsSettings.tabs.schools' },
  {
    key: 'educational-administrations',
    labelKey: 'lookupsSettings.tabs.educationalAdministrations',
  },
  { key: 'referral-partners', labelKey: 'lookupsSettings.tabs.referralPartners' },
  { key: 'branches', labelKey: 'lookupsSettings.tabs.branches' },
];

/**
 * Settings → Lookups (`/settings/lookups`, Admin-only — gated by `RoleGuard` in `router.tsx`).
 * Standard ARIA tabs pattern (10-design-system.md §4.9): one tab per reference list (grades,
 * agreed schools, educational administrations, referral partners — T151, DEC-REF8; branches — T174,
 * docs/design/19-branch-scoping-ux.md §1.1: no new nav item, just a 5th tab here); only the active
 * tabpanel is mounted/in the tab order. Each panel's CRUD is delegated to `LookupManager`, reused
 * as-is across all five resources — the branches lookup is byte-for-byte the same base shape as the
 * other four (it is additionally the first BRANCH-SCOPED lookup on read, but that's a server-side
 * concern invisible to this shell — see `lookups.api.ts#getBranches`).
 */
export function LookupsPage() {
  const { t, i18n } = useTranslation();
  const [activeIndex, setActiveIndex] = useState(0);
  const tabRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const isRtl = i18n.language.startsWith('ar');

  function focusTab(index: number) {
    const normalized = (index + TABS.length) % TABS.length;
    setActiveIndex(normalized);
    tabRefs.current[normalized]?.focus();
  }

  function handleKeyDown(e: KeyboardEvent<HTMLButtonElement>, index: number) {
    switch (e.key) {
      case 'ArrowRight':
        e.preventDefault();
        focusTab(index + (isRtl ? -1 : 1));
        break;
      case 'ArrowLeft':
        e.preventDefault();
        focusTab(index + (isRtl ? 1 : -1));
        break;
      case 'Home':
        e.preventDefault();
        focusTab(0);
        break;
      case 'End':
        e.preventDefault();
        focusTab(TABS.length - 1);
        break;
      default:
        break;
    }
  }

  const activeTab = TABS[activeIndex];

  return (
    <section className="fa-lookups-page">
      <div className="fa-lookups-page__header">
        <h1 className="fa-lookups-page__title">{t('lookupsSettings.pageTitle')}</h1>
        <p className="fa-lookups-page__description">{t('lookupsSettings.pageDescription')}</p>
      </div>

      <div
        role="tablist"
        aria-label={t('lookupsSettings.tabsAriaLabel')}
        className="fa-lookups-page__tablist"
      >
        {TABS.map((tab, index) => (
          <button
            key={tab.key}
            ref={(el) => {
              tabRefs.current[index] = el;
            }}
            type="button"
            role="tab"
            id={`lookups-tab-${tab.key}`}
            aria-selected={activeIndex === index}
            aria-controls={`lookups-tabpanel-${tab.key}`}
            tabIndex={activeIndex === index ? 0 : -1}
            className={`fa-lookups-page__tab ${
              activeIndex === index ? 'fa-lookups-page__tab--active' : ''
            }`}
            onClick={() => setActiveIndex(index)}
            onKeyDown={(e) => handleKeyDown(e, index)}
          >
            {t(tab.labelKey)}
          </button>
        ))}
      </div>

      <div
        role="tabpanel"
        id={`lookups-tabpanel-${activeTab.key}`}
        aria-labelledby={`lookups-tab-${activeTab.key}`}
        tabIndex={0}
        className="fa-lookups-page__tabpanel"
      >
        <LookupManager
          key={activeTab.key}
          resource={activeTab.key}
          resourceLabelKey={activeTab.labelKey}
        />
      </div>
    </section>
  );
}
